'use client'

import { useEffect } from 'react'
import { Button, Link } from '@carbon/react'

const BREVO_SCRIPT_ID = 'brevo-conversations-loader'
const BREVO_SCRIPT_SRC = 'https://conversations-widget.brevo.com/brevo-conversations.js'

declare global {
  interface Window {
    Cookiebot?: { consent?: { statistics?: boolean; marketing?: boolean } }
    BrevoConversationsID?: string
    BrevoConversationsSetup?: {
      buttonStyle: string
      buttonPosition: string
      colors: { buttonText: string; buttonBg: string }
      zIndex: number
    }
    BrevoConversations?: ((...args: unknown[]) => void) & { q?: unknown[][] }
  }
}

function loadBrevo() {
  if (document.getElementById(BREVO_SCRIPT_ID)) return

  window.BrevoConversationsID = '6a7aedcf6b45c5291c0206b5'
  window.BrevoConversations = window.BrevoConversations || ((...args: unknown[]) => {
    const queue = window.BrevoConversations as ((...queuedArgs: unknown[]) => void) & { q?: unknown[][] }
    queue.q = queue.q || []
    queue.q.push(args)
  })
  window.BrevoConversationsSetup = {
    buttonStyle: 'round',
    buttonPosition: 'br',
    colors: { buttonText: '#ffffff', buttonBg: '#0f62fe' },
    zIndex: 10000,
  }

  const script = document.createElement('script')
  script.id = BREVO_SCRIPT_ID
  script.async = true
  // The chat is a user-requested support function, not marketing tracking.
  // Cookiebot's automatic blocking otherwise makes its availability depend on
  // the browser's stored marketing-consent cookie.
  script.dataset.cookieconsent = 'ignore'
  script.src = BREVO_SCRIPT_SRC
  document.head.appendChild(script)
}

function openBrevoChat() {
  loadBrevo()
  window.BrevoConversations?.('openChat', true)
}

export function BrevoChatLink({ label, href = '/support' }: { label: string; href?: string }) {
  return (
    <Link
      href={href}
      onClick={(event) => {
        event.preventDefault()
        openBrevoChat()
      }}
    >
      {label}
    </Link>
  )
}

export function BrevoChatButton({ label, kind = 'primary' }: { label: string; kind?: 'primary' | 'secondary' | 'tertiary' }) {
  return (
    <Button
      kind={kind}
      onClick={openBrevoChat}
    >
      {label}
    </Button>
  )
}

export function BrevoChat() {
  useEffect(() => {
    // Brevo rejects some synthetic/audit clients with HTTP 403. Loading the
    // support widget only after a deliberate user interaction keeps the chat
    // available to visitors without putting the third-party request on the
    // initial rendering path or polluting automated performance audits.
    const loadOnInteraction = () => {
      window.removeEventListener('pointerdown', loadOnInteraction)
      window.removeEventListener('keydown', loadOnInteraction)
      loadBrevo()
    }

    window.addEventListener('pointerdown', loadOnInteraction, { once: true, passive: true })
    window.addEventListener('keydown', loadOnInteraction, { once: true })

    return () => {
      window.removeEventListener('pointerdown', loadOnInteraction)
      window.removeEventListener('keydown', loadOnInteraction)
    }
  }, [])

  return null
}
